import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { PEOPLE_KEY, PERSONS_RESOURCE, type Person } from './types';

/**
 * "Możliwy duplikat: Jan Kowalski — Połącz": an account's person whose e-mail another person (without an account)
 * also has. Never linked by itself (a shared mailbox is not the same human); "Połącz" merges them on purpose: the
 * older person takes the account, the functions and the crew, the account's person disappears.
 */
export function DuplicateNotice({ person }: { person: Person }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const merge = useMutation({
    mutationFn: () => api<Person>(`/api/people/persons/${person.id}/merge-duplicate`, { method: 'POST', body: { version: person.version } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: PEOPLE_KEY }),
  });
  const dup = person.possibleDuplicate;
  if (!dup || !me) return null;
  // review I1 ("Kto widzi"): "Połącz" moves the account to the other person — the owner's alone
  const mayMerge = me.isOwner && can(me, PERSONS_RESOURCE, 'edit');
  return (
    <div className="dup-notice" role="note">
      Możliwy duplikat: {dup.name} (ten sam e-mail)
      {mayMerge && (
        <>
          {' — '}
          <button className="btn ghost small" type="button" disabled={merge.isPending} onClick={() => merge.mutate()} aria-label={`Połącz z: ${dup.name}`}>
            {merge.isPending ? 'Łączenie…' : 'Połącz'}
          </button>
        </>
      )}
      {merge.error && (
        <span className="alert" role="alert">
          {' '}
          {merge.error instanceof ApiError ? merge.error.message : 'Nie udało się połączyć'}
        </span>
      )}
    </div>
  );
}
